/**
 * Small allocation-free vector and closest-point helpers for the chime simulation.
 */
import type { Vec3 } from '../types';

export const dot = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
export const len = (a: Vec3) => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);

export function set(out: Vec3, x: number, y: number, z: number): Vec3 {
  out.x = x; out.y = y; out.z = z;
  return out;
}
export function copy(out: Vec3, a: Vec3): Vec3 {
  out.x = a.x; out.y = a.y; out.z = a.z;
  return out;
}
export function sub(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out.x = a.x - b.x; out.y = a.y - b.y; out.z = a.z - b.z;
  return out;
}
export function addScaled(out: Vec3, a: Vec3, b: Vec3, s: number): Vec3 {
  out.x = a.x + b.x * s; out.y = a.y + b.y * s; out.z = a.z + b.z * s;
  return out;
}
export function scale(out: Vec3, a: Vec3, s: number): Vec3 {
  out.x = a.x * s; out.y = a.y * s; out.z = a.z * s;
  return out;
}
export function cross(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  const x = a.y * b.z - a.z * b.y;
  const y = a.z * b.x - a.x * b.z;
  const z = a.x * b.y - a.y * b.x;
  out.x = x; out.y = y; out.z = z;
  return out;
}
export function normalize(out: Vec3, a: Vec3): Vec3 {
  const l = len(a);
  if (l > 1e-12) { out.x = a.x / l; out.y = a.y / l; out.z = a.z / l; }
  return out;
}

/** Parameter t in [0,1] of the point on segment ab closest to p. */
export function closestOnSegment(a: Vec3, b: Vec3, p: Vec3): number {
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const l2 = dx * dx + dy * dy + dz * dz;
  if (l2 < 1e-12) return 0;
  const t = ((p.x - a.x) * dx + (p.y - a.y) * dy + (p.z - a.z) * dz) / l2;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

export interface SegSegResult { s: number; t: number }

/**
 * Closest points between segments p1q1 and p2q2 (Ericson, Real-Time Collision Detection
 * 5.1.9). Writes parameters s (on segment 1) and t (on segment 2) into `out`.
 */
export function closestSegSeg(p1: Vec3, q1: Vec3, p2: Vec3, q2: Vec3, out: SegSegResult): void {
  const d1x = q1.x - p1.x, d1y = q1.y - p1.y, d1z = q1.z - p1.z;
  const d2x = q2.x - p2.x, d2y = q2.y - p2.y, d2z = q2.z - p2.z;
  const rx = p1.x - p2.x, ry = p1.y - p2.y, rz = p1.z - p2.z;
  const a = d1x * d1x + d1y * d1y + d1z * d1z;
  const e = d2x * d2x + d2y * d2y + d2z * d2z;
  const f = d2x * rx + d2y * ry + d2z * rz;
  const EPS = 1e-12;
  let s = 0, t = 0;
  if (a <= EPS && e <= EPS) { out.s = 0; out.t = 0; return; }
  if (a <= EPS) {
    s = 0; t = clamp01(f / e);
  } else {
    const c = d1x * rx + d1y * ry + d1z * rz;
    if (e <= EPS) {
      t = 0; s = clamp01(-c / a);
    } else {
      const b = d1x * d2x + d1y * d2y + d1z * d2z;
      const denom = a * e - b * b;
      s = denom !== 0 ? clamp01((b * f - c * e) / denom) : 0;
      t = (b * s + f) / e;
      if (t < 0) { t = 0; s = clamp01(-c / a); }
      else if (t > 1) { t = 1; s = clamp01((b - c) / a); }
    }
  }
  out.s = s; out.t = t;
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
